/**
 * Expense Service
 *
 * Handles all expense-related operations including CRUD operations,
 * file uploads for receipts, and budget calculations.
 *
 * @module services/expenseService
 */

import { supabase } from './supabase';
import { STORAGE_BUCKETS } from '../utils/constants';
import { calculateBudgetVariance } from '../utils/calculations';
import { arrayToCSV } from '../utils/exportHelpers';
// Mismo bucket ('receipts') y MISMO bug que ya se cerró para
// `purchases.attachment_url`. Se REUSAN esos helpers a propósito en vez de
// escribir una segunda versión: la duplicación es justo lo que hizo que este
// bug viviera en dos tablas a la vez. Si cambia la política de firmado, cambia
// en un solo lugar. (`resolveAttachmentUrl` está fijado al bucket `receipts`,
// que es exactamente `STORAGE_BUCKETS.RECEIPTS`.)
import { extractStoragePath, resolveAttachmentUrl } from './purchaseService';
// L10 — proyecto efectivo de la línea de compra (línea > encabezado), la misma
// regla que usan projectService y el carril MCP.
import { fetchOpenCommittedLines, committedAmountByProject } from '../utils/purchaseProjectTag';

/**
 * Devuelve la fila con un `receipt_url` VIVO.
 *
 * En la base se guarda el PATH estable dentro del bucket; la signed URL se
 * firma fresca en cada lectura. Las filas viejas guardaron una signed URL
 * completa: `extractStoragePath` (dentro de `resolveAttachmentUrl`) le saca el
 * path, así que también se re-firman solas sin migrar datos.
 */
const withLiveReceiptUrl = async (expense) => {
  if (!expense?.receipt_url) return expense;
  return { ...expense, receipt_url: await resolveAttachmentUrl(expense.receipt_url) };
};

const withLiveReceiptUrls = async (rows) =>
  Promise.all((rows || []).map(withLiveReceiptUrl));

/**
 * Path del objeto en Storage para un gasto.
 *
 * `expenses` NO tiene columna `receipt_path` (verificado contra prod:
 * information_schema.columns devuelve 0 filas). El código anterior leía
 * `expense.receipt_path` — siempre `undefined` — así que el borrado del
 * archivo NUNCA se ejecutaba y cada gasto eliminado dejaba su comprobante
 * huérfano en el bucket. El path se deriva de `receipt_url`, que es donde
 * realmente está.
 */
const receiptStoragePath = (expense) =>
  extractStoragePath(STORAGE_BUCKETS.RECEIPTS, expense?.receipt_url);

/**
 * List expenses with optional filters
 * @param {Object} filters - Filter options
 * @param {string} filters.projectId - Filter by project
 * @param {string} filters.taskId - Filter by task
 * @param {string} filters.category - Filter by category
 * @param {string} filters.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} filters.dateTo - End date (YYYY-MM-DD)
 * @param {string} filters.search - Search vendor name
 * @param {number} filters.page - Page number (0-indexed)
 * @param {number} filters.pageSize - Items per page
 * @returns {Promise<{data, count, error}>}
 */
export const listExpenses = async (filters = {}) => {
  try {
    let query = supabase
      .from('expenses')
      .select(`
        *,
        project:projects(id, project_number, title),
        task:tasks(id, task_number, title),
        created_by_profile:profiles!created_by(id, full_name)
      `, { count: 'exact' });

    // Apply filters
    if (filters.projectId) {
      query = query.eq('project_id', filters.projectId);
    }

    if (filters.taskId) {
      query = query.eq('task_id', filters.taskId);
    }

    if (filters.category) {
      query = query.eq('category', filters.category);
    }

    if (filters.dateFrom) {
      query = query.gte('expense_date', filters.dateFrom);
    }

    if (filters.dateTo) {
      query = query.lte('expense_date', filters.dateTo);
    }

    // Apply pagination
    const page = filters.page || 0;
    const pageSize = filters.pageSize || 20;

    if (filters.search) {
      const s = filters.search.trim();
      // Use DB function that supports UUID::text cast for global search
      const { data: searchResults, error: rpcError } = await supabase
        .rpc('search_expenses', { search_term: s });

      if (rpcError) throw rpcError;

      // Apply other filters client-side on search results
      let filtered = searchResults || [];
      if (filters.projectId) filtered = filtered.filter(e => e.project_id === filters.projectId);
      if (filters.taskId) filtered = filtered.filter(e => e.task_id === filters.taskId);
      if (filters.category) filtered = filtered.filter(e => e.category === filters.category);
      if (filters.dateFrom) filtered = filtered.filter(e => e.expense_date >= filters.dateFrom);
      if (filters.dateTo) filtered = filtered.filter(e => e.expense_date <= filters.dateTo);

      // Sort by date descending
      filtered.sort((a, b) => new Date(b.expense_date) - new Date(a.expense_date));

      // Paginate
      const total = filtered.length;
      const from = page * pageSize;
      const paged = filtered.slice(from, from + pageSize);

      // Fetch related data for paged results
      const ids = paged.map(e => e.id);
      if (ids.length > 0) {
        const { data: enriched } = await supabase
          .from('expenses')
          .select(`
            *,
            project:projects(id, project_number, title),
            task:tasks(id, task_number, title),
            created_by_profile:profiles!created_by(id, full_name)
          `)
          .in('id', ids)
          .order('expense_date', { ascending: false });

        return {
          data: await withLiveReceiptUrls(enriched || paged),
          count: total,
          error: null,
        };
      }

      return { data: [], count: 0, error: null };
    }

    const from = page * pageSize;
    const to = from + pageSize - 1;

    query = query
      .range(from, to)
      .order('expense_date', { ascending: false });

    const { data, count, error } = await query;

    if (error) throw error;

    return { data: await withLiveReceiptUrls(data), count, error: null };
  } catch (error) {
    console.error('List expenses error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * Fila CRUDA del gasto, sin re-firmar el comprobante.
 *
 * Existe para las rutas de ESCRITURA (update/delete): esas necesitan el PATH
 * guardado, no una URL viva. Si usaran `getExpense`, tendrían que firmar la
 * URL y volver a parsearle el path — un round-trip que solo funciona mientras
 * el formato de la signed URL no cambie. El valor de la columna es la fuente.
 */
const getExpenseRaw = async (expenseId) => {
  const { data, error } = await supabase
    .from('expenses')
    .select('id, project_id, receipt_url')
    .eq('id', expenseId)
    .single();

  if (error) throw error;
  return data;
};

/**
 * Get expense by ID
 * @param {string} expenseId - Expense ID
 * @returns {Promise<{data, error}>}
 */
export const getExpense = async (expenseId) => {
  try {
    const { data, error } = await supabase
      .from('expenses')
      .select(`
        *,
        project:projects(id, project_number, title, quoted_amount),
        task:tasks(id, task_number, title),
        created_by_profile:profiles!created_by(id, full_name)
      `)
      .eq('id', expenseId)
      .single();

    if (error) throw error;

    return { data: await withLiveReceiptUrl(data), error: null };
  } catch (error) {
    console.error('Get expense error:', error);
    return { data: null, error };
  }
};

/**
 * Upload receipt file to Supabase Storage
 *
 * Devuelve el PATH, no una URL. Antes firmaba acá mismo una URL a 1 hora y ese
 * valor se persistía en `expenses.receipt_url`: una hora después el
 * comprobante del gasto era un enlace muerto con el archivo intacto en el
 * bucket. La URL se firma en cada lectura (`withLiveReceiptUrl`).
 *
 * @param {File} file - Receipt file
 * @param {string} projectId - Project ID for organizing files
 * @returns {Promise<{path, error}>}
 */
const uploadReceipt = async (file, projectId) => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${projectId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKETS.RECEIPTS)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    return { path: data.path, error: null };
  } catch (error) {
    console.error('Upload receipt error:', error);
    return { path: null, error };
  }
};

/**
 * Get budget status for a project
 * @param {string} projectId - Project ID
 * @returns {Promise<{data, error}>}
 */
const getProjectBudgetStatus = async (projectId) => {
  try {
    // Get project quoted amount
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select('quoted_amount')
      .eq('id', projectId)
      .single();

    if (projectError) throw projectError;

    // Get total expenses
    const { data: expenses, error: expensesError } = await supabase
      .from('expenses')
      .select('amount')
      .eq('project_id', projectId);

    if (expensesError) throw expensesError;

    // Committed amount: open purchase order lines (approved/sent/partial),
    // mirroring the MCP agent's check_budget_status calculation so the alert
    // shown right after recording an expense matches what the agent reports
    // for the same project (#276).
    // L10 — cuarta copia de este cálculo. Se resuelve el proyecto EFECTIVO
    // (línea > encabezado) igual que projectService y que el carril MCP: si
    // sólo una de las cuatro contara la línea heredada, el aviso posterior al
    // gasto diría un número distinto del de la pantalla del proyecto.
    const committedAmount = committedAmountByProject(
      await fetchOpenCommittedLines(supabase, [projectId])
    ).get(projectId) || 0;

    // #679: fiel. `null` = nadie decidió el presupuesto; 0 = cotizado a costo.
    const quotedAmount = project.quoted_amount ?? null;
    const spentAmount = expenses?.reduce((sum, exp) => sum + (exp.amount || 0), 0) || 0;

    // Spreads { variance, variancePercentage, remaining, status, isOverBudget }.
    // #469: this used to spread a `percentage` key while every consumer
    // (ExpenseForm's budget warning modal, BudgetAlertBanner, ProjectDetail)
    // reads `variancePercentage` — so `variancePercentage >= 80` was always
    // `undefined >= 80` and the over-budget modal never appeared. Keep this
    // shape aligned with `calculateBudgetVariance`; do not rename in transit.
    const budgetStatus = calculateBudgetVariance(quotedAmount, spentAmount + committedAmount);

    return {
      data: {
        quotedAmount,
        spentAmount,
        committedAmount,
        ...budgetStatus,
      },
      error: null,
    };
  } catch (error) {
    console.error('Get project budget status error:', error);
    return { data: null, error };
  }
};

/**
 * Presupuesto del proyecto **como quedaría** si se registrara un gasto de
 * `pendingAmount`, SIN escribir nada.
 *
 * Existe porque la advertencia de presupuesto tiene que llegar ANTES del
 * INSERT. Antes se calculaba con `getProjectBudgetStatus` recién *después* de
 * `recordExpense`, con la fila ya escrita: el modal preguntaba "¿deseas
 * continuar?" sobre algo que ya había pasado, "Cancelar" no deshacía nada y
 * "Continuar" mandaba un segundo INSERT idéntico. Un aviso que llega después
 * del hecho no es un aviso, es un reporte.
 *
 * Devuelve la misma forma que `getProjectBudgetStatus` (`quotedAmount`,
 * `spentAmount`, `committedAmount`, `variance`, `variancePercentage`,
 * `remaining`, `status`, `isOverBudget`) con `pendingAmount` sumado a lo
 * gastado, para que el modal muestre el número proyectado y no el actual.
 *
 * @param {string} projectId - Project ID
 * @param {number} pendingAmount - Monto del gasto todavía no registrado
 * @returns {Promise<{data, error}>}
 */
export const previewExpenseBudgetImpact = async (projectId, pendingAmount = 0) => {
  try {
    if (!projectId) throw new Error('projectId es requerido');

    const { data: current, error } = await getProjectBudgetStatus(projectId);
    if (error) throw error;

    const pending = Number(pendingAmount) || 0;
    const projectedSpent = (current.spentAmount || 0) + pending;

    return {
      data: {
        ...current,
        spentAmount: projectedSpent,
        pendingAmount: pending,
        ...calculateBudgetVariance(
          current.quotedAmount ?? null,
          projectedSpent + (current.committedAmount || 0)
        ),
      },
      error: null,
    };
  } catch (error) {
    console.error('Preview expense budget impact error:', error);
    return { data: null, error };
  }
};

/**
 * Record new expense
 * @param {Object} expenseData - Expense data
 * @param {string} expenseData.project_id - Project ID
 * @param {string} expenseData.task_id - Task ID (optional)
 * @param {string} expenseData.category - Expense category
 * @param {number} expenseData.amount - Expense amount
 * @param {string} expenseData.vendor - Vendor name
 * @param {string} expenseData.expense_date - Expense date (YYYY-MM-DD)
 * @param {string} expenseData.notes - Notes (optional)
 * @param {File} receiptFile - Receipt file (optional)
 * @returns {Promise<{data, budgetAlert, error}>}
 */
export const recordExpense = async (expenseData, receiptFile = null) => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Upload receipt if provided
    let receiptPath = null;

    if (receiptFile) {
      const uploadResult = await uploadReceipt(receiptFile, expenseData.project_id);
      if (uploadResult.error) {
        console.warn('Receipt upload failed:', uploadResult.error);
        // Continue without receipt
      } else {
        receiptPath = uploadResult.path;
      }
    }

    // `receipt_url` guarda el PATH del objeto en el bucket privado `receipts`,
    // NO una signed URL (esas mueren en 1 hora). Se re-firma en cada lectura.
    const { data, error } = await supabase
      .from('expenses')
      .insert({
        ...expenseData,
        receipt_url: receiptPath,
        created_by: user.id,
      })
      .select(`
        *,
        project:projects(id, project_number, title, quoted_amount),
        created_by_profile:profiles!created_by(id, full_name)
      `)
      .single();

    if (error) throw error;

    // Check budget status
    const budgetStatus = await getProjectBudgetStatus(expenseData.project_id);

    return {
      data: await withLiveReceiptUrl(data),
      budgetAlert: budgetStatus.data,
      error: null,
    };
  } catch (error) {
    console.error('Record expense error:', error);
    return { data: null, budgetAlert: null, error };
  }
};

/**
 * Update expense
 * @param {string} expenseId - Expense ID
 * @param {Object} updates - Expense updates
 * @param {File} receiptFile - New receipt file (optional)
 * @returns {Promise<{data, error}>}
 */
export const updateExpense = async (expenseId, updates, receiptFile = null) => {
  try {
    // Fila cruda a propósito: acá hace falta el PATH guardado, no una URL
    // firmada. Reescribir una signed URL en la columna sería reintroducir el
    // bug que este cambio cierra.
    const currentExpense = await getExpenseRaw(expenseId);

    const currentPath = receiptStoragePath(currentExpense);
    let receiptPath = currentPath || currentExpense.receipt_url || null;

    if (receiptFile) {
      // Delete old receipt if exists
      if (currentPath) {
        await supabase.storage
          .from(STORAGE_BUCKETS.RECEIPTS)
          .remove([currentPath]);
      }

      // Upload new receipt
      const uploadResult = await uploadReceipt(receiptFile, currentExpense.project_id);
      if (uploadResult.error) {
        console.warn('Receipt upload failed:', uploadResult.error);
      } else {
        receiptPath = uploadResult.path;
      }
    }

    // `receipt_url` guarda el PATH, no una signed URL. Ver `uploadReceipt`.
    const { data, error } = await supabase
      .from('expenses')
      .update({
        ...updates,
        receipt_url: receiptPath,
      })
      .eq('id', expenseId)
      .select(`
        *,
        project:projects(id, project_number, title, quoted_amount),
        created_by_profile:profiles!created_by(id, full_name)
      `)
      .single();

    if (error) throw error;

    return { data: await withLiveReceiptUrl(data), error: null };
  } catch (error) {
    console.error('Update expense error:', error);
    return { data: null, error };
  }
};

/**
 * Delete expense
 * @param {string} expenseId - Expense ID
 * @returns {Promise<{success, error}>}
 */
export const deleteExpense = async (expenseId) => {
  try {
    // Fila cruda: se necesita el PATH guardado, no una URL firmada.
    const expense = await getExpenseRaw(expenseId);

    // Delete receipt from storage if exists.
    // Antes esto leía `expense.receipt_path`, columna que NO existe en la
    // tabla: siempre `undefined`, o sea que el archivo nunca se borraba y
    // cada gasto eliminado dejaba su comprobante huérfano en el bucket.
    const receiptPath = receiptStoragePath(expense);
    if (receiptPath) {
      await supabase.storage
        .from(STORAGE_BUCKETS.RECEIPTS)
        .remove([receiptPath]);
    }

    // Delete expense
    const { error } = await supabase
      .from('expenses')
      .delete()
      .eq('id', expenseId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('Delete expense error:', error);
    return { success: false, error };
  }
};

/**
 * Get project expenses summary
 * @param {string} projectId - Project ID
 * @returns {Promise<{data, error}>}
 */
export const getProjectExpenses = async (projectId) => {
  try {
    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .eq('project_id', projectId)
      .order('expense_date', { ascending: false });

    if (error) throw error;

    // Calculate totals by category
    const totals = {
      total: 0,
      byCategory: {},
    };

    data.forEach(expense => {
      totals.total += expense.amount || 0;
      const category = expense.category || 'other';
      totals.byCategory[category] = (totals.byCategory[category] || 0) + expense.amount;
    });

    return {
      data: {
        expenses: await withLiveReceiptUrls(data),
        totals,
      },
      error: null,
    };
  } catch (error) {
    console.error('Get project expenses error:', error);
    return { data: null, error };
  }
};

// `getReceiptSignedUrl(path)` vivía acá. Se borró junto con su único
// consumidor (`ExpenseList.handleImageClick`, que sacaba el path de la URL
// vencida con un regex y volvía a firmar). Ese par existía solo para
// compensar que la base guardaba una signed URL muerta. Ahora la base guarda
// el path y el re-firmado pasa una sola vez, en la lectura del servicio
// (`withLiveReceiptUrl`), así que toda fila que llega a la UI ya trae una URL
// viva. Si hiciera falta firmar algo suelto, el resolvedor compartido es
// `purchaseService.resolveAttachmentUrl`.

/**
 * Export expenses to CSV
 * @param {Array} expenses - Array of expenses
 * @returns {string} CSV string
 */
export const exportExpensesToCSV = (expenses) => {
  const headers = [
    { key: 'expense_date', label: 'Fecha' },
    { key: 'project_number', label: 'Proyecto' },
    { key: 'task_number', label: 'Tarea' },
    { key: 'category', label: 'Categoría' },
    { key: 'vendor', label: 'Proveedor' },
    { key: 'amount', label: 'Monto' },
    { key: 'receipt', label: 'Recibo' },
    { key: 'notes', label: 'Notas' },
  ];

  const rows = expenses.map(expense => ({
    expense_date: expense.expense_date,
    project_number: expense.project?.project_number || '-',
    task_number: expense.task?.task_number || '-',
    category: expense.category,
    vendor: expense.vendor || '-',
    amount: expense.amount,
    receipt: expense.receipt_url ? 'Sí' : 'No',
    notes: expense.notes || '-',
  }));

  return arrayToCSV(rows, headers);
};

const expenseService = {
  listExpenses,
  getExpense,
  previewExpenseBudgetImpact,
  recordExpense,
  updateExpense,
  deleteExpense,
  getProjectExpenses,
  exportExpensesToCSV,
};

export default expenseService;
